import React, { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import * as authService from '../../services/authService'
import {
  REWARD_FULFILLMENT_LABELS,
  getMyRewardRedemptions,
  type RewardRedemptionHistoryItem,
} from '../../services/pointsService'
import { HistoryCollapseCard, type HistoryDetailRow } from '../history/HistoryCollapseCard'
import './pointsLayout.css'

const PAGE_SIZE = 20

const formatNumber = (value: number | null | undefined, digits = 0) =>
  Number(value || 0).toLocaleString('th-TH', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })

const formatDateTime = (value?: string | null) => {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString('th-TH', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const buildRows = (item: RewardRedemptionHistoryItem): HistoryDetailRow[] => {
  const shipping = item.shippingInfo || {}
  const rows: HistoryDetailRow[] = [
    { label: 'รางวัล', value: item.rewardName },
    { label: 'ประเภท', value: item.rewardType === 'credit' ? 'เครดิต' : 'ของรางวัล' },
    { label: 'สถานะ', value: item.status === 'failed' ? 'ไม่สำเร็จ' : 'สำเร็จ' },
    {
      label: 'สถานะจัดส่ง',
      value: REWARD_FULFILLMENT_LABELS[item.fulfillmentStatus] || item.fulfillmentStatus,
    },
    { label: 'คะแนนที่ใช้', value: `${formatNumber(item.pointsCost)} คะแนน` },
  ]

  if (item.rewardType === 'credit') {
    rows.push({ label: 'เครดิตที่ได้', value: `${formatNumber(item.creditAmount, 2)} บาท`, tone: 'positive' })
  }

  if (item.beforePoints != null && item.afterPoints != null) {
    rows.push({
      label: 'คะแนนคงเหลือ',
      value: `${formatNumber(item.beforePoints)} → ${formatNumber(item.afterPoints)}`,
    })
  }

  if (item.rewardType === 'item') {
    rows.push(
      { label: 'ชื่อผู้รับ', value: shipping.name || '-' },
      { label: 'เบอร์ติดต่อ', value: shipping.phone || '-' },
      { label: 'ที่อยู่/หมายเหตุ', value: shipping.address || '-', tone: 'muted' }
    )
  }

  rows.push(
    { label: 'เวลาทำรายการ', value: formatDateTime(item.createdAt) },
    { label: 'อัปเดตล่าสุด', value: formatDateTime(item.updatedAt) }
  )

  if (item.note) {
    rows.push({ label: 'หมายเหตุจากระบบ', value: item.note, tone: 'muted' })
  }

  return rows
}

const PointsRewardHistoryPage: React.FC = () => {
  const [items, setItems] = useState<RewardRedemptionHistoryItem[]>([])
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const loadHistory = useCallback(async (pageNum = 1) => {
    const currentUser = authService.getCurrentUser()
    const tenantId = currentUser?.tenantId
    const userId = currentUser?.id
    if (!tenantId || !userId) {
      setItems([])
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError(null)
      const response = await getMyRewardRedemptions({ tenantId, userId, page: pageNum, limit: PAGE_SIZE })
      setItems(response.data || [])
      setPage(response.pagination?.page || pageNum)
      setTotalPages(Math.max(1, response.pagination?.totalPages || 1))
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'โหลดประวัติไม่สำเร็จ')
      setItems([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadHistory(1)
  }, [loadHistory])

  const handleCopyRef = async (refId: string) => {
    try {
      await navigator.clipboard.writeText(refId)
    } catch {
      // ignore clipboard errors
    }
  }

  return (
    <>
      <div className="c_quinary text-center">
        <h3 className="c_tertiary font_md">ประวัติแลกรางวัล</h3>
      </div>

      <div className="light_sd border_10 p-3 p-md-4 bg_op_secondary bg_blur mt-3 mb-3 reward-history-panel">
        <div className="reward-history-hints mb-3 mb-md-4">
          <div className="reward-history-hint">
            <span className="reward-history-hint__icon bg_gd_quaternary">
              <i className="fas fa-history c_tertiary" aria-hidden="true" />
            </span>
            <div>
              <div className="c_primary font_sm fw-semibold">ประวัติแลกรางวัล</div>
              <div className="c_secondary font_sm">ดูรายการย้อนหลัง คะแนนที่ใช้ และสถานะการจัดส่ง</div>
            </div>
          </div>
        </div>

        {loading && (
          <div className="reward-history-state bg_secondary border_10 bdc_primary ins_sd">
            <div className="reward-history-state__icon bg_gd_tertiary">
              <i className="fas fa-spinner fa-spin c_tertiary" aria-hidden="true" />
            </div>
            <p className="c_primary font_md mb-0">กำลังโหลดประวัติ...</p>
          </div>
        )}

        {!loading && error && (
          <div className="reward-history-state bg_secondary border_10 bdc_primary ins_sd">
            <div className="reward-history-state__icon bg_gd_primary">
              <i className="fas fa-exclamation-triangle c_tertiary" aria-hidden="true" />
            </div>
            <p className="c_danger font_md mb-3">{error}</p>
            <button
              type="button"
              className="link_pmr box_row justify-content-center font_rg bg_gd_quaternary border_10 ins_sd border-0 px-4 py-2"
              onClick={() => void loadHistory(page)}
            >
              <span className="c_tertiary font_sm">ลองโหลดอีกครั้ง</span>
            </button>
          </div>
        )}

        {!loading && !error && items.length === 0 && (
          <div className="reward-history-state bg_secondary border_10 bdc_primary ins_sd">
            <div className="reward-history-state__icon bg_gd_quaternary">
              <i className="fas fa-gift c_tertiary" aria-hidden="true" />
            </div>
            <h5 className="c_primary font_md mb-2">ยังไม่มีประวัติการแลกรางวัล</h5>
            <p className="c_secondary font_sm mb-4">
              เมื่อคุณแลกของรางวัลหรือเครดิตด้วยคะแนน รายการจะแสดงที่นี่พร้อมสถานะการจัดส่ง
            </p>
            <Link
              to="/points/rewards"
              className="link_pmr box_row justify-content-center font_rg bg_gd_quaternary border_10 ins_sd text-decoration-none px-4 py-2"
            >
              <span className="c_tertiary font_sm d-flex align-items-center gap-2">
                <i className="fas fa-arrow-right" aria-hidden="true" />
                ไปหน้าแลกรางวัล
              </span>
            </Link>
          </div>
        )}

        {!loading && !error && items.length > 0 && (
          <>
            <div className="reward-history-summary bg_gd_secondary border_5 px-3 py-2 mb-3 d-flex align-items-center justify-content-between flex-wrap gap-2">
              <span className="c_tertiary font_sm">
                <i className="fas fa-list-ul me-2" aria-hidden="true" />
                พบ {items.length} รายการ{totalPages > 1 ? ` (หน้า ${page}/${totalPages})` : ''}
              </span>
              <span className="c_quinary font_sm">แตะการ์ดเพื่อดูรายละเอียด</span>
            </div>
            <div className="d-flex flex-column gap-2">
            {items.map((item) => {
              const refId = item.redemptionId
              const fulfillmentLabel = REWARD_FULFILLMENT_LABELS[item.fulfillmentStatus] || item.fulfillmentStatus
              const leftIcon =
                item.status === 'failed'
                  ? 'reject'
                  : item.fulfillmentStatus === 'delivered' || item.rewardType === 'credit'
                    ? 'success'
                    : 'pending'

              return (
                <HistoryCollapseCard
                  key={refId}
                  id={refId}
                  leftIcon={leftIcon}
                  refId={refId}
                  onCopyRef={() => void handleCopyRef(refId)}
                  header={(
                    <div className="w-100 pe-md-4">
                      <div className="c_primary font_sm">{item.rewardName}</div>
                      <small className="c_tertiary d-block">
                        ใช้ {formatNumber(item.pointsCost)} คะแนน · {formatDateTime(item.createdAt)}
                      </small>
                      <small className="c_tertiary d-block">
                        {item.rewardType === 'credit' ? 'รางวัลเครดิต' : 'รางวัลของ'} · {fulfillmentLabel}
                      </small>
                    </div>
                  )}
                  rows={buildRows(item)}
                />
              )
            })}
            </div>
          </>
        )}

        {!loading && !error && totalPages > 1 && (
          <div className="d-flex align-items-center justify-content-between gap-2 mt-3">
            <button
              type="button"
              className="bg_op_secondary c_primary border_5 border-0 px-3 py-2"
              disabled={page <= 1}
              onClick={() => void loadHistory(page - 1)}
            >
              ก่อนหน้า
            </button>
            <div className="c_tertiary">หน้า {page} / {totalPages}</div>
            <button
              type="button"
              className="bg_op_secondary c_primary border_5 border-0 px-3 py-2"
              disabled={page >= totalPages}
              onClick={() => void loadHistory(page + 1)}
            >
              ถัดไป
            </button>
          </div>
        )}
      </div>
    </>
  )
}

export default PointsRewardHistoryPage
